import {
  Title,
  Text,
  Paper,
  Tabs,
  Divider,
  Flex,
  Box,
  Button,
  LoadingOverlay,
  ThemeIcon,
  Tooltip,
  SimpleGrid,
  Select,
  Modal,
  Checkbox,
} from "@mantine/core";
import PersonalDetails from "../EmptyCompanyProfile/EmptyProfileForms/PersonalDetails";
import BasicCompanyInfo from "../EmptyCompanyProfile/EmptyProfileForms/EmptyBasicInfo";
import BusinessOverview from "../EmptyCompanyProfile/EmptyProfileForms/EmptyBusinessOverview";
import CapabilitiesOfferings from "../EmptyCompanyProfile/EmptyProfileForms/EmptyCapabilities";
import GeographyMarkets from "../EmptyCompanyProfile/EmptyProfileForms/EmptyGeographyMarkets";
import ProfileGenerateModal from "../ProfileGenerateModal/ProfileGenerateModal";
import CreateProfileModal from "../ProfileGenerateModal/CreateProfileModal";
import { useEffect, useCallback, useState } from "react";
import { MultiStepLoader } from "@/components/shared/MultiStepLoader";
import { useNavigate } from "react-router-dom";
import {
  countriesData,
  getIndustryOptions,
  getRegionOptionsForSelect,
} from "@/components/shared/constants/general.const";
import { headquartersValueForForm } from "@/shared/constants/signup.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { errorNotification } from "@/shared/utils/errorNotification";
import { useDashboardState } from "./statecomponent/DashboardState";
import { IconGift, IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";
import ProductsServicesTabContent from "@/components/profile/ProductsServicesTabContent";
import DocumentsTabContent from "@/components/profile/DocumentsTabContent";
import ImagesTabContent from "@/components/profile/ImagesTabContent";

const regionOptions = getRegionOptionsForSelect();

interface DashboardPageProps {
  mode?: "create" | "edit";
}

function CreateProfilePage({ mode = "create" }: DashboardPageProps) {
  const navigate = useNavigate();
  const state = useDashboardState(mode);
  const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);
  const [showAuthorityConfirm, setShowAuthorityConfirm] = useState(false);
  const [authorityChecked, setAuthorityChecked] = useState(false);
  const [isAuthoritySaving, setIsAuthoritySaving] = useState(false);

  const AUTHORITY_CONFIRM_MESSAGE =
    "I hereby confirm that I am authorised to create and manage this account on behalf of the company. I further confirm that all information provided is true and accurate, and I understand that any misrepresentation may result in account suspension and legal action.";

  const countryOptions = countriesData.countryGroups.map((group) => ({
    group: group.group,
    items: group.items.map((item) => item.name),
  }));

  const industryOptions = getIndustryOptions();

  // Locked fields configuration for verified companies
  const lockedFieldsWhenVerified = {
    // Basic Company Info
    companyLegalName: true,
    country: true,
    registrationNumber: true,
    registrationIdType: true,
    yearOfIncorporation: true,
    companyEmail: true,
    state: true,

    // Geography & Markets
    headquartersCountry: true,
    headquartersCity: true,

    // Additional Details
    companyRegisteredAddress: true,
    additionalCompanyStatus: true,
    directorName: true,
    directorDesignation: true,
    verificationBusinessType: true,
  };

  const isFieldLocked = (fieldName: string) => {
    if (fieldName === "companyEmail") return true;
    if (mode !== "edit") return false;
    if (!state.isVerified) return false;
    if (state.isAdminBlocked) return false;
    return lockedFieldsWhenVerified[fieldName as keyof typeof lockedFieldsWhenVerified] || false;
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!state.userDetails || !(state.userDetails as any).company) {
        state.setIsPageLoading(false);
        if (mode !== "edit") {
          state.setShowIntroModal(true);
        }
        return;
      }

      const companyIdFromUser = (state.userDetails as any).company.id;
      state.setCompanyId(companyIdFromUser);

      try {
        const company = await trpc.company.getById.query({ companyId: companyIdFromUser });

        if (company) {
          if (mode === "edit") {
            const allowedStatuses = ["VERIFIED", "SYSTEM_VERIFIED", "ACTION_REQUIRED", "DRAFT"];
            if (!allowedStatuses.includes(company.status || "")) {
              navigate("/profile", { replace: true });
              return;
            }
          } else if (mode === "create") {
            if ((company as any).website) {
              navigate("/profile", { replace: true });
              return;
            }
          }

          state.setCompanyStatus(company.status || null);
          state.setCompanyGtid((company as any).gtid || null);
          state.setIsVerified(company.status === "VERIFIED" || company.status === "SYSTEM_VERIFIED");
          state.setIsAdminBlocked((company as any).adminBlocked === true);

          if ((company as any).logo) state.setExistingLogo((company as any).logo);
          if ((company as any).images && (company as any).images.length > 0) {
            state.setExistingImages((company as any).images);
          }

          // Extract Products and Services from company data
          if ((company as any).productServices && Array.isArray((company as any).productServices)) {
            const products = (company as any).productServices.filter((ps: any) => ps.type === "PRODUCT");
            const services = (company as any).productServices.filter((ps: any) => ps.type === "SERVICE");
            state.setProducts(products);
            state.setServices(services);
          }

          // Handle Documents
          if ((company as any).documents) {
            const docs = (company as any).documents;
            // All documents here are treated as optional "additional documents"
            state.setExistingDocs(docs);
          }

          state.setFormData((prev) => ({
            ...prev,
            // Personal Details from User
            userName: (company as any).user?.name || "",
            userDesignation: (company as any).user?.designation || "",
            userPhoneNumber: (company as any).user?.phoneNumber?.replace(/^\+\d+\s*/, "") || "",
            userPhoneCountryCode: (company as any).user?.phoneNumber?.match(/^\+\d+/)?.[0] || "",
            // Company Details
            companyLegalName: (company as any).legalName || "",
            tradeName: (company as any).tradeName || "",
            state: (company as any).state || "",
            registrationNumber: (company as any).registrationNumber || "",
            registrationIdType: (() => {
              const t = (company as any).registrationIdType;
              const hc = headquartersValueForForm((company as any).headquartersCountry);
              if (t) {
                if (hc === "New Zealand" && t === "LOCAL_REGISTRY") return "NZBN";
                return t;
              }
              const reg = ((company as any).registrationNumber || "").trim().toUpperCase();
              if (hc === "India") return null;
              if (/^[0-9A-Z]{18}[0-9]{2}$/.test(reg)) return "LEI";
              if (reg) return hc === "New Zealand" ? "NZBN" : "LOCAL_REGISTRY";
              return hc === "New Zealand" ? "NZBN" : "LOCAL_REGISTRY";
            })(),
            yearOfIncorporation: (company as any).yearOfIncorporation
              ? new Date((company as any).yearOfIncorporation, 0, 1)
              : null,
            companyWebsite: (company as any).website || "",
            companyEmail: (company as any).email || "",
            phoneCountryCode: (company as any).phoneCountryCode || "",
            phoneNumber: (company as any).phoneNumber || "",
            companyDescription: (company as any).description || "",
            industryCategory: (company as any).industryCategory || [],
            subIndustry: (company as any).subIndustry || "",
            whoAmI: (company as any).whoAmI || [],
            collaborationIntent: (company as any).collaborationIntent || [],
            coreCapabilities: (company as any).coreCapabilities || [],
            secondaryCapabilities: (company as any).secondaryCapabilities || [],
            keyDifferentiators: (company as any).keyDifferentiators || "",
            technologies: (company as any).technologies || [],
            additionalTechInfrastructure: (company as any).additionalTechInfrastructure || [],
            country: headquartersValueForForm((company as any).headquartersCountry),
            headquartersCountry: headquartersValueForForm((company as any).headquartersCountry),
            headquartersCity: headquartersValueForForm((company as any).headquartersCity),
            currentAddress: (company as any).currentAddress || "",
            operatingCountries: (company as any).operatingCountries || [],
            preferredRegions: (company as any).preferredCollaborationRegions || [],
            companyRegisteredAddress: (company as any).additionalDetails?.companyRegisteredAddress || "",
            nationalIndustryClassificationCode:
              (company as any).additionalDetails?.nationalIndustryClassificationCode || "",
            additionalCompanyStatus: (company as any).additionalDetails?.companyStatus || "",
            directorName: (company as any).additionalDetails?.directorName || "",
            directorDesignation: (company as any).additionalDetails?.directorDesignation || "",
            directorEmail: (company as any).additionalDetails?.directorEmail || "",
            paidupCapitalBand: (company as any).additionalDetails?.paidupCapitalBand || "",
            turnoverBand: (company as any).additionalDetails?.turnoverBand || "",
            verificationBusinessType: (company as any).additionalDetails?.verificationBusinessType || "",
          }));

          if (!(company as any).description && mode !== "edit") {
            state.setShowIntroModal(true);
          }
        }
      } catch (err) {
        handleTrpcError(err);
      } finally {
        state.setIsPageLoading(false);
      }
    };

    fetchCompanyData();
  }, [state.userDetails, navigate]);

  const toStringArray = (val: any): string[] => {
    if (!val) return [];
    if (typeof val === "string") return [val];
    if (!Array.isArray(val)) return [];
    return val.map((item) => (typeof item === "string" ? item : String(item?.name ?? item?.value ?? item ?? "")));
  };

  const handleAiProfileGenerated = useCallback(
    (data: any) => {
      state.setFormData((prev) => ({
        ...prev,
        companyLegalName: data.data.legal_name || "",
        tradeName: data.data.trade_name || "",
        companyDescription: data.data.company_description || "",
        industryCategory: toStringArray(data.data.industry_category),
        subIndustry: toStringArray(data.data.sub_industry),
        country: data.data.country || "",
        headquartersCountry: data.data.country || "",
        headquartersCity: data.data.city || "",
        yearOfIncorporation: data.data.year_of_incorporation ? new Date(data.data.year_of_incorporation, 0, 1) : null,
        companyWebsite: data.data.company_website || "",
        phoneNumber: data.data.phone_number || "",
        directorName: data.data.director_name || "",
        directorEmail: data.data.director_email || "",
        directorDesignation: data.data.director_designation || "",
        coreCapabilities: toStringArray(data.data.core_capabilities),
        secondaryCapabilities: toStringArray(data.data.secondary_capabilities),
        keyDifferentiators: data.data.key_differentiators || "",
        technologies: toStringArray(data.data.technologies_infrastructure),
        operatingCountries: toStringArray(data.data.operating_countries),
        preferredRegions: toStringArray(data.data.preferred_collaboration_regions),
        collaborationIntent: toStringArray(data.data.collaboration_intent),
      }));

      if (data.s3_urls && data.s3_urls.image_urls && Array.isArray(data.s3_urls.image_urls)) {
        state.setAiImages(data.s3_urls.image_urls);
      }

      if (data.data.products && Array.isArray(data.data.products)) {
        const newProducts = data.data.products.map((p: any) => ({
          clientId: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: p.name,
          description: p.description,
        }));
        state.setProducts(newProducts);
      }

      if (data.data.services && Array.isArray(data.data.services)) {
        const newServices = data.data.services.map((s: any) => ({
          clientId: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: s.name,
          description: s.description,
        }));
        state.setServices(newServices);
      }

      state.setActiveTab("company-profile");
    },
    [state]
  );

  const getSectionIdForField = (fieldName: string, tab: string) => {
    if (tab === "images") {
      if (fieldName.toLowerCase().includes("additional doc")) return "documents-section";
      return "images-section";
    }

    if (
      [
        "Name",
        "Designation",
        "Company Legal Name",
        "Country",
        "Registration ID Type",
        "Registration Number",
        "Company Website",
        "Company Email",
        "Phone Number",
        "Headquarters Country",
        "Headquarters City",
      ].includes(fieldName)
    ) {
      return "basic-company-info-section";
    }
    if (["Company Description", "Industry Category", "Who Am I?"].includes(fieldName)) {
      return "business-overview-section";
    }
    if (fieldName.startsWith("Product ") || fieldName.startsWith("Service ")) {
      return "products-services-section";
    }
    if (fieldName === "Core Capabilities") return "capabilities-offerings-section";
    if (fieldName.includes("verification")) {
      return "personal-details-section";
    }
    return "basic-company-info-section";
  };

  const handleTopSubmitClick = () => {
    const companyErrors = state.invalidFields["company-profile"] || [];
    const imageErrors = state.invalidFields.images || [];

    if (companyErrors.length > 0 || imageErrors.length > 0) {
      const targetTab = companyErrors.length > 0 ? "company-profile" : "images";
      const firstMissingField = companyErrors[0] || imageErrors[0];
      const sectionId = getSectionIdForField(firstMissingField, targetTab);

      state.setActiveTab(targetTab);
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);

      errorNotification({
        title: "Required field missing",
        message: `${firstMissingField} is required.`,
      });
      return;
    }

    if (mode === "create") {
      setShowAuthorityConfirm(true);
      return;
    }

    document.getElementById("create-profile-submit-btn")?.click();
  };

  const handleAuthorityAcceptAndSubmit = async () => {
    if (!state.companyId) {
      errorNotification({ title: "Error", message: "Company not found. Please try again." });
      return;
    }
    if (!authorityChecked) {
      errorNotification({ title: "Confirmation required", message: "Please accept the confirmation to continue." });
      return;
    }

    setIsAuthoritySaving(true);
    try {
      await trpc.company.recordAuthorityAcceptance.mutate({
        companyId: state.companyId,
        message: AUTHORITY_CONFIRM_MESSAGE,
      });
      setShowAuthorityConfirm(false);
      document.getElementById("create-profile-submit-btn")?.click();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsAuthoritySaving(false);
    }
  };

  const handleOpenCreateModal = useCallback(() => {
    state.setShowIntroModal(false);
    state.setShowCreateModal(true);
  }, [state]);

  return (
    <Flex direction={"column"} style={{ height: "100vh", overflow: "hidden" }}>
      <LoadingOverlay visible={state.isPageLoading} overlayProps={{ blur: 2 }} />
      <Modal
        opened={showAuthorityConfirm}
        onClose={() => {
          if (isAuthoritySaving) return;
          setShowAuthorityConfirm(false);
        }}
        title="Confirmation"
        centered
        size="lg"
        closeOnClickOutside={!isAuthoritySaving}
        closeOnEscape={!isAuthoritySaving}
      >
        <Text fz="sm" c="slate.7" mb="md" style={{ lineHeight: 1.5 }}>
          {AUTHORITY_CONFIRM_MESSAGE}
        </Text>

        <Checkbox
          checked={authorityChecked}
          onChange={(e) => setAuthorityChecked(e.currentTarget.checked)}
          label="I accept and confirm the above statement"
          mb="lg"
          disabled={isAuthoritySaving}
        />

        <Flex justify="flex-end" gap="sm">
          <Button
            variant="outline"
            color="gray"
            onClick={() => setShowAuthorityConfirm(false)}
            disabled={isAuthoritySaving}
          >
            DENY
          </Button>
          <Button onClick={handleAuthorityAcceptAndSubmit} loading={isAuthoritySaving}>
            Accept & Create Profile
          </Button>
        </Flex>
      </Modal>

      <Flex p="lg" justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap="sm">
        <Flex align="center" gap="md" style={{ flexShrink: 0 }}>
          {mode === "edit" && (
            <Button
              variant="outline"
              color="gray"
              p={8}
              onClick={() => navigate("/profile")}
              styles={{
                root: {
                  borderColor: "#E5E7EB",
                  borderRadius: 8,
                  minWidth: "auto",
                  height: "auto",
                },
              }}
            >
              <IconArrowLeft size={18} color="#374151" />
            </Button>
          )}
          <Box>
            <Title order={2} fz={24} fw={400}>
              {mode === "edit" ? "Edit Company Profile" : "Company Profile Builder"}
            </Title>
            {mode !== "edit" && (
              <Text c="slate.6" fz="sm">
                Complete your profile to unlock collaboration discovery
              </Text>
            )}
          </Box>
        </Flex>

        <Flex gap="sm" mt={{ base: "sm", md: 0 }} style={{ flexShrink: 0 }}>
          {mode !== "edit" && <Button onClick={() => state.setShowIntroModal(true)}>Generate with AI</Button>}
          <Button onClick={handleTopSubmitClick}>{mode === "edit" ? "Save" : "Create Profile"}</Button>
        </Flex>
      </Flex>

      <Divider />

      <Flex align="flex-start" px="md" py="xl" style={{ flex: 1, minHeight: 0 }}>
        {/* Full-width content */}
        <Box w="100%" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <Tabs
            value={state.activeTab}
            onChange={state.setActiveTab}
            style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
          >
            <Tabs.List
              styles={{
                list: {
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                  borderBottom: "2px solid #04305c",
                  boxShadow: "none",
                  gap: 0,
                },
              }}
            >
              <Tabs.Tab
                value="company-profile"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={{ base: "xs", sm: "sm" }}
                style={{
                  backgroundColor: state.activeTab === "company-profile" ? "#04305c" : "#ebf0ff",
                  color: state.activeTab === "company-profile" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                  border: state.activeTab === "company-profile" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                  fontWeight: state.activeTab === "company-profile" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {state.invalidFields["company-profile"]?.length > 0 ? (
                      <Tooltip
                        label={
                          <Box>
                            <Text fz="xs" fw={700} mb={4} c="white">
                              Missing required fields:
                            </Text>
                            {state.invalidFields["company-profile"].map((field) => (
                              <Text key={field} fz="xs" c="gray.3">
                                • {field}
                              </Text>
                            ))}
                          </Box>
                        }
                        position="top"
                        withArrow
                        multiline
                        color="dark.8"
                      >
                        <ThemeIcon size="xs" color="red" variant="transparent" style={{ cursor: "pointer" }}>
                          <IconAlertCircle size={20} />
                        </ThemeIcon>
                      </Tooltip>
                    ) : null}
                  </Box>
                }
              >
                Company Profile
              </Tabs.Tab>
              <Tabs.Tab
                value="images"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={{ base: "xs", sm: "sm" }}
                style={{
                  backgroundColor: state.activeTab === "images" ? "#04305c" : "#ebf0ff",
                  color: state.activeTab === "images" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                  border: state.activeTab === "images" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                  fontWeight: state.activeTab === "images" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {state.invalidFields["images"]?.length > 0 ? (
                      <Tooltip
                        label={
                          <Box>
                            <Text fz="xs" fw={700} mb={4} c="white">
                              Missing required fields:
                            </Text>
                            {state.invalidFields["images"].map((field) => (
                              <Text key={field} fz="xs" c="gray.3">
                                • {field}
                              </Text>
                            ))}
                          </Box>
                        }
                        position="top"
                        withArrow
                        multiline
                        color="dark.8"
                      >
                        <ThemeIcon size="xs" color="red" variant="transparent" style={{ cursor: "pointer" }}>
                          <IconAlertCircle size={20} />
                        </ThemeIcon>
                      </Tooltip>
                    ) : null}
                  </Box>
                }
              >
                Images and Documents
              </Tabs.Tab>
            </Tabs.List>

            <Box style={{ flex: 1, overflow: "auto" }}>
              <Tabs.Panel value="company-profile" p="sm">
                <Title id="basic-company-info-section" order={4} mb={4}>
                  Basic Company Information
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  Provide your company's legal and contact details. Fields marked with * are required.
                </Text>

                <BasicCompanyInfo
                  formData={state.formData}
                  updateForm={state.updateForm}
                  logo={state.logo}
                  setLogo={state.handleLogoChange}
                  countryOptions={countryOptions}
                  existingLogo={state.existingLogo}
                  onRemoveLogo={state.handleRemoveLogo}
                  isFieldLocked={isFieldLocked}
                  rightOfLogoContent={
                    <Flex gap="sm" wrap="wrap" justify="flex-end" align="stretch">
                      <Paper
                        p="sm"
                        radius="lg"
                        w={{ base: "100%", md: "auto" }}
                        style={{
                          border: "1px solid #E5E7EB",
                          flex: "0 0 auto",
                          minWidth: 200,
                        }}
                      >
                        <Text fz="xs" c="slate.6" tt="uppercase" fw={500}>
                          Company ID
                        </Text>
                        <Text fz="md" fw={600} c="primary.7">
                          {state.companyGtid || "N/A"}
                        </Text>
                      </Paper>

                      {mode === "create" && (
                        <Paper
                          p="sm"
                          radius="lg"
                          w={{ base: "100%", md: 360 }}
                          bg="linear-gradient(to bottom, #F0FDF4, #FFFFFF)"
                          style={{
                            border: "1px solid #B9F8CF",
                            flex: "0 0 auto",
                            maxWidth: "100%",
                          }}
                        >
                          <Flex gap="sm" align="flex-start">
                            <ThemeIcon size={24} radius="xl" color="teal" variant="light">
                              <IconGift size={14} />
                            </ThemeIcon>
                            <Box>
                              <Text fz="sm" fw={600} mb={4}>
                                20 Credits Joining Bonus
                              </Text>
                              <Text fz="xs" c="slate.6" style={{ whiteSpace: "normal" }}>
                                Complete your profile to unlock 20 free credits
                              </Text>
                            </Box>
                          </Flex>
                        </Paper>
                      )}
                    </Flex>
                  }
                />

                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" mt="md">
                  <Select
                    label="Paidup capital Band in USD $"
                    placeholder="Select band"
                    data={[
                      { value: "0 - $100000", label: "0 - $100000" },
                      { value: "$100001 - $500000", label: "$100001 - $500000" },
                      { value: "Above $500000", label: "Above $500000" },
                    ]}
                    value={(state.formData as any).paidupCapitalBand || ""}
                    onChange={(value) => state.updateForm("paidupCapitalBand", value || "")}
                  />
                  <Select
                    label="Turnover Band in USD $"
                    placeholder="Select band"
                    data={[
                      { value: "0 - 500000", label: "0 - 500000" },
                      { value: "500001 - 2000000", label: "500001 - 2000000" },
                      { value: "Above 2000000", label: "Above 2000000" },
                    ]}
                    value={(state.formData as any).turnoverBand || ""}
                    onChange={(value) => state.updateForm("turnoverBand", value || "")}
                  />
                  <Box />
                </SimpleGrid>

                <Divider my="xl" />

                <Title id="business-overview-section" order={4} mb={4}>
                  Business Overview
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  Describe your company's business focus, industry, and operating model.
                </Text>

                <BusinessOverview
                  formData={state.formData}
                  updateForm={state.updateForm}
                  industryOptions={industryOptions}
                />

                <Divider my="xl" />

                <Title id="products-services-section" order={4} mb={4}>
                  Products & Services
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  List your company's key products and services.
                </Text>

                <ProductsServicesTabContent
                  isViewMode={false}
                  products={state.products}
                  services={state.services}
                  onAddProduct={state.handleAddProduct}
                  onUpdateProduct={state.handleUpdateProduct}
                  onRemoveProduct={state.handleRemoveProduct}
                  onAddService={state.handleAddService}
                  onUpdateService={state.handleUpdateService}
                  onRemoveService={state.handleRemoveService}
                  productErrors={state.productErrors}
                  serviceErrors={state.serviceErrors}
                />

                <Divider my="xl" />

                <Title id="capabilities-offerings-section" order={4} mb={4}>
                  Capabilities & Offerings
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  Define your core manufacturing capabilities, technologies, and unique differentiators.
                </Text>

                <CapabilitiesOfferings
                  formData={state.formData}
                  updateForm={state.updateForm}
                  coreCapabilityInput={state.coreCapabilityInput}
                  setCoreCapabilityInput={state.setCoreCapabilityInput}
                  secondaryCapabilityInput={state.secondaryCapabilityInput}
                  setSecondaryCapabilityInput={state.setSecondaryCapabilityInput}
                  technologyInput={state.technologyInput}
                  setTechnologyInput={state.setTechnologyInput}
                  addTag={state.addTag}
                  removeTag={state.removeTag}
                />

                <Divider my="xl" />

                <Title id="geography-markets-section" order={4} mb={4}>
                  Geography & Markets
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  Define your geographic presence, operating regions, and target markets.
                </Text>

                <GeographyMarkets
                  formData={state.formData}
                  updateForm={state.updateForm}
                  countryOptions={countryOptions}
                  regionOptions={regionOptions}
                // isFieldLocked={isFieldLocked}
                />

                <Divider my="xl" />

                <Title id="personal-details-section" order={4} mb={4}>
                  Personal Details
                </Title>
                <Text fz="sm" c="slate.6" mb="lg">
                  Provide your personal information to be shared with matched companies. Fields marked with * are
                  required.
                </Text>

                <PersonalDetails
                  formData={state.formData}
                  updateForm={state.updateForm}
                  isFieldLocked={isFieldLocked}
                />

                <Flex justify="flex-end">
                  <Button
                    size="md"
                    onClick={state.handleSubmit}
                    loading={state.isSubmitting}
                    id="create-profile-submit-btn"
                    style={{ display: "none" }}
                  >
                    {mode === "edit" ? "Save Changes" : "Create Profile"}
                  </Button>
                </Flex>
              </Tabs.Panel>

              <Tabs.Panel value="images" p="xl">
                <Box id="images-section">
                  <ImagesTabContent
                    company={{ id: state.companyId, images: state.existingImages }}
                    activeTab={state.activeTab}
                    aiImages={state.aiImages}
                    removeAiImage={state.removeAiImage}
                    refreshCompany={async () => {
                      const id = state.companyId;
                      if (id) {
                        const companyData = await trpc.company.getById.query({ companyId: id });
                        if ((companyData as any).images) {
                          state.setExistingImages((companyData as any).images);
                        }
                      }
                    }}
                  />
                </Box>

                <Divider my="xl" />

                <Box id="documents-section">
                  <DocumentsTabContent
                    isViewMode={false}
                    additionalDocs={state.additionalDocs}
                    existingDocs={state.existingDocs}
                    onAddAdditionalDoc={state.handleAddAdditionalDoc}
                    onUpdateAdditionalDoc={state.handleUpdateAdditionalDoc}
                    onRemoveAdditionalDoc={state.handleRemoveAdditionalDoc}
                    onDeleteExistingDoc={state.handleDeleteExistingDoc}
                  />
                </Box>
              </Tabs.Panel>
            </Box>
          </Tabs>
          <ProfileGenerateModal
            opened={state.showIntroModal}
            onClose={() => state.setShowIntroModal(false)}
            onGenerate={handleOpenCreateModal}
          />

          <CreateProfileModal
            opened={state.showCreateModal}
            onClose={() => state.setShowCreateModal(false)}
            onSuccess={(data) => {
              setIsGeneratingProfile(false);
              handleAiProfileGenerated(data);
            }}
            onGeneratingStart={() => setIsGeneratingProfile(true)}
            onGeneratingEnd={() => setIsGeneratingProfile(false)}
          />

          <MultiStepLoader
            loading={isGeneratingProfile}
            generationKey={isGeneratingProfile ? 1 : 0}
            duration={3500}
            loadingStates={[
              { text: "Analyzing your company information" },
              { text: "Identifying industry and capabilities" },
              { text: "Building your business overview" },
              { text: "Mapping geography and markets" },
              { text: "Generating products and services" },
              { text: "Finalizing your profile" },
            ]}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

export default CreateProfilePage;
